import {
    type FilterConfigBarItem,
    isFilterConfigBarItemDefault,
    isFilterConfigBarItemPrice,
    isFilterConfigBarItemSearchable,
} from '@rocco/ui/filter-sort/types/filter-config-bar';
import {
    type FilterState,
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import { UIConfigurationError } from '@rocco/exceptions/ui-configuration-error';
import { useState } from 'react';
import { FiltersBarSingleValue } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarSingleValue';
import { FiltersBarSearchableList } from '@rocco/ui/filter-sort/components/filters-bar/elements/FilterSearchableList';
import { FiltersBarMultipleValues } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarMultipleValues';
import { FiltersBarPrice } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarPrice';
import { FiltersBarLocation } from '@rocco/ui/filter-sort/components/filters-bar/elements/FiltersBarLocation';

export interface FilterBarElementProps<T, V> {
    filterConfigItem: T;
    filterState: V;
    onFilterStateUpdate: (state: V, refetch: boolean) => void;
}

export const FiltersBarElement = (props: {
    filterConfigItem: FilterConfigBarItem;
    filterState: FilterState | null;
}) => {
    const { filterConfigItem, filterState } = props;
    const { searchFilters } = useFiltersContext();
    const { filterStates, updateFilter, getUpdatedFilterStates } =
        searchFilters;

    if (filterState === null) {
        throw new UIConfigurationError(
            `Filter has a null state: ${filterConfigItem.filterKey}`,
        );
    }

    const [state, setState] = useState<FilterState>(filterState);

    const updateState = (newState: FilterState, refetch: boolean) => {
        setState(newState);

        const newFilterStates = getUpdatedFilterStates(
            filterStates,
            filterConfigItem.filterKey,
            newState,
        );

        updateFilter(newFilterStates, refetch);
    };

    if (isFilterConfigBarItemDefault(filterConfigItem)) {
        if (isSingleValueFilterState(state)) {
            return (
                <FiltersBarSingleValue
                    filterConfigItem={filterConfigItem}
                    filterState={state}
                    onFilterStateUpdate={updateState}
                />
            );
        }

        if (isLocationFilterState(state)) {
            return (
                <FiltersBarLocation
                    filterConfigItem={filterConfigItem}
                    filterState={state}
                    onFilterStateUpdate={updateState}
                />
            );
        }
    }

    if (
        isFilterConfigBarItemSearchable(filterConfigItem) &&
        isSearchableListFilterState(state)
    ) {
        return (
            <FiltersBarSearchableList
                filterConfigItem={filterConfigItem}
                filterState={state}
                onFilterStateUpdate={updateState}
            />
        );
    }

    if (
        isFilterConfigBarItemDefault(filterConfigItem) &&
        isMultipleValuesFilterState(state)
    ) {
        return (
            <FiltersBarMultipleValues
                filterConfigItem={filterConfigItem}
                filterState={state}
                onFilterStateUpdate={updateState}
            />
        );
    }

    if (
        isFilterConfigBarItemPrice(filterConfigItem) &&
        isRangeFilterState(state)
    ) {
        return (
            <FiltersBarPrice
                filterConfigItem={filterConfigItem}
                filterState={state}
                onFilterStateUpdate={updateState}
            />
        );
    }

    return null;
};
